import { AlertTriangle, BarChart2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/providers/AuthProvider";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getWorstSeverity, buildLimitMessage } from "@/components/LimitBlocker";

type LimitIssue = { candidates: string | null; jobs: string | null } | null;

/**
 * Inline banner shown to admins when the org has reached (but not exceeded by >10%) its limits.
 * Styled to match DuplicateCandidatesBanner. Always visible — not dismissible.
 * Placed in the main content area alongside AIStatusBanner.
 */
export const LimitBanner = () => {
    const { limitIssue, isSuperAdmin, isAdmin, isLimitInGracePeriod } = useAuth();
    const { t } = useTranslation();
    const location = useLocation();

    // Only show to org admins, never to super admins or on admin pages
    if (isSuperAdmin || !isAdmin) return null;
    if (location.pathname.startsWith("/admin")) return null;

    // Show during grace period after a limit issue is set
    if (!limitIssue || !isLimitInGracePeriod) return null;

    const limitedThings = buildLimitMessage(limitIssue as any, t);

    return (
        <div className="w-full bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4">
            <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 min-w-0">
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                    <div className="flex items-center gap-2 min-w-0">
                        <p className="text-xs font-medium text-amber-900">
                            {t("billing.limitBanner.title")}
                        </p>
                        <div className="flex items-center gap-1 text-amber-700">
                            <BarChart2 className="h-3 w-3 shrink-0" />
                            <span className="text-xs truncate">
                                {t("billing.limitBanner.description", { limitedThings })}
                            </span>
                        </div>
                    </div>
                </div>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.location.href = "mailto:billing@digger.be"}
                    className="flex items-center gap-1 text-xs h-7 px-2 border-amber-300 text-amber-700 hover:bg-amber-100 shrink-0"
                >
                    {t("billing.contactUs")}
                </Button>
            </div>
        </div>
    );
};
